import { AtIcon, CirclePauseIcon, LinkIcon, PhoneIcon, PinIcon } from "@xola/icons";
import { Alert, Button } from "@xola/ui-kit";
import { SecondaryButton } from "common";
import React from "react";
import { useParams } from "react-router-dom";
import { XolaPartner, XolaPartnerStatus } from "types";
import { NoTranslate } from "@/components/Common/NoTranslate";
import { ContentLayout } from "@/components/Layout/ContentLayout";
import { Loader } from "@/components/Loader";
import { useResource } from "@/hooks/useResource";
import { getDefaultLogoUrl } from "@/models/Seller";

const ContactDetail = ({ icon: Icon, detail }: { icon: React.FC; detail?: string }) =>
    detail ? (
        <NoTranslate as="p" className="flex items-center space-x-1 text-sm">
            <Icon />
            <span>{detail}</span>
        </NoTranslate>
    ) : null;

export const PartnerDetails = () => {
    const { id } = useParams<{ id: string }>();
    const { data: partner, isLoading } = useResource<XolaPartner>(["partners", id], { expand: "partnerSeller" });

    if (isLoading) {
        return <Loader />;
    }

    if (!partner?.partnerSeller) {
        return null;
    }

    const { partnerSeller: seller } = partner;

    return (
        <ContentLayout isWide classNames={{ root: "!mb-0 pb-0" }}>
            <div className="space-y-4 divide-y divide-gray-light pl-6">
                <div className="flex space-x-14 py-12">
                    <div className="flex max-w-80 flex-col">
                        <h3>Partner Profile</h3>
                        <p className="text-gray-darker">Partner&apos;s basic information</p>
                    </div>
                    <div className="flex w-1/2 flex-col space-y-2 pt-2 pl-32">
                        {partner.status === XolaPartnerStatus.PENDING && (
                            <Alert color="warning" className="ml-85">
                                <div className="flex items-center p-1">
                                    <CirclePauseIcon size="large" />
                                    <div className="pl-2">
                                        <h4 className="text-sm font-bold">Pending Connection</h4>
                                        <span className="text-xs">{seller.name} has not yet accepted your request</span>
                                    </div>
                                    <div className="space-x-2 pl-32">
                                        <SecondaryButton size="small">Resend</SecondaryButton>
                                        <Button color="danger" size="small">
                                            Cancel Request
                                        </Button>
                                    </div>
                                </div>
                            </Alert>
                        )}
                        <div className="flex space-x-4">
                            <img
                                alt={`Logo for ${seller.name}`}
                                src={getDefaultLogoUrl(seller)}
                                className="no-translate h-14 w-14"
                            />
                            <div>
                                <NoTranslate as="p" className="text-md font-bold">
                                    {seller.name}
                                </NoTranslate>
                                <ContactDetail icon={LinkIcon} detail={seller.website} />
                                <ContactDetail icon={PinIcon} detail={seller.streetAddress} />
                                <div className="pt-2">
                                    <span className="text-sm font-bold">About the Business</span>
                                    <p>{seller.preferences?.network.aboutBusiness}</p>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-1 pt-4">
                            <h2 className="text-lg font-bold">Contact Details</h2>
                            <ContactDetail icon={PhoneIcon} detail={seller.preferences?.network.phone} />
                            <NoTranslate as="p" className="flex items-center space-x-1 text-sm">
                                <AtIcon />
                                <span className="underline">{seller.preferences?.network.email}</span>
                            </NoTranslate>
                        </div>
                    </div>
                </div>
            </div>
        </ContentLayout>
    );
};
